import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

// A tab that stays continuously focused across a deploy never triggers
// useVersionCheck's focus/visibility-based reload — it only fires when a
// tab regains focus. If that same tab then navigates into a lazy-loaded
// route (React.lazy) whose JS chunk was removed by the new deploy, the
// dynamic import rejects with an uncaught error and React unmounts the
// whole tree: a blank white screen with no recovery short of a manual hard
// refresh. This boundary catches that specific failure and reloads once,
// automatically. Any other render error falls back to a manual-reload
// message instead of looping.
const RELOAD_FLAG_KEY = "orbit_chunk_reload_attempted";

const isChunkLoadError = (error: Error): boolean => {
  const message = error?.message || "";
  return (
    error?.name === "ChunkLoadError" ||
    /loading chunk|loading css chunk|failed to fetch dynamically imported module/i.test(
      message
    )
  );
};

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error) {
    if (!isChunkLoadError(error)) {
      return;
    }
    // Guard against a reload loop in case this ever fires for a reason
    // other than a genuinely stale chunk reference.
    if (sessionStorage.getItem(RELOAD_FLAG_KEY)) {
      return;
    }
    sessionStorage.setItem(RELOAD_FLAG_KEY, "1");
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      // Only skip straight to a blank frame the first time — reload is
      // about to kick in via componentDidCatch. If the chunk error keeps
      // happening even after that one attempt (sessionStorage flag already
      // set), fall through to the manual-reload message below instead of
      // going blank forever.
      if (this.state.isChunkError && !sessionStorage.getItem(RELOAD_FLAG_KEY)) {
        return null;
      }
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            gap: "12px",
            fontFamily: "sans-serif",
          }}
        >
          <p>Something went wrong.</p>
          <button onClick={() => window.location.reload()}>Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}
